import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "react-query";
import styled from "styled-components";
import { motion } from "framer-motion";
import { FiSearch, FiMapPin, FiFilter, FiGrid, FiList } from "react-icons/fi";
import { vendorsAPI } from "../../services/api";

const VendorsContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 2rem 0;
`;

const VendorsContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;

  h1 {
    font-size: 2.5rem;
    font-weight: 700;
    color: #2d5016;
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.125rem;
    color: #666;
    max-width: 600px;
    margin: 0 auto;
  }
`;

const SearchAndFilters = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const SearchBar = styled.div`
  position: relative;
  margin-bottom: 1rem;

  input {
    width: 100%;
    padding: 0.75rem 1rem;
    padding-left: 2.5rem;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    font-size: 1rem;
    transition: border-color 0.3s ease;

    &:focus {
      outline: none;
      border-color: #2d5016;
    }
  }

  .search-icon {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #666;
    font-size: 1.125rem;
  }
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 150px;

  label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #333;
  }

  select {
    padding: 0.5rem;
    border: 2px solid #e0e0e0;
    border-radius: 6px;
    font-size: 0.875rem;
    background: white;

    &:focus {
      outline: none;
      border-color: #2d5016;
    }
  }
`;

const ViewToggle = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-left: auto;

  button {
    padding: 0.5rem;
    border: 2px solid #e0e0e0;
    background: white;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.3s ease;

    &.active {
      background: #2d5016;
      color: white;
      border-color: #2d5016;
    }

    &:hover:not(.active) {
      border-color: #2d5016;
    }
  }
`;

const VendorsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;

  &.list-view {
    grid-template-columns: 1fr;
  }
`;

const VendorCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const VendorImage = styled.div`
  height: 200px;
  background: linear-gradient(135deg, #2d5016, #4a7c59);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 3rem;
  position: relative;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
  }
`;

const VendorInfo = styled.div`
  padding: 1.5rem;

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #2d5016;
    margin-bottom: 0.5rem;
  }

  .cuisine {
    color: #666;
    font-size: 0.875rem;
    margin-bottom: 1rem;
  }

  .rating {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1rem;

    .stars {
      display: flex;
      gap: 0.25rem;
      color: #ffc107;
    }

    .rating-text {
      color: #666;
      font-size: 0.875rem;
    }
  }

  .location {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #666;
    font-size: 0.875rem;
    margin-bottom: 1rem;
  }

  .description {
    color: #666;
    font-size: 0.875rem;
    line-height: 1.5;
    margin-bottom: 1rem;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

const ViewButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.75rem;
  background: #2d5016;
  color: white;
  text-decoration: none;
  border-radius: 6px;
  font-weight: 500;
  transition: background 0.3s ease;

  &:hover {
    background: #1a3a0e;
  }
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 1.125rem;
  color: #666;
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #dc3545;

  h3 {
    margin-bottom: 1rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;

  h3 {
    margin-bottom: 1rem;
    color: #333;
  }
`;

const Vendors = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [cuisineFilter, setCuisineFilter] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  const [sortBy, setSortBy] = useState("rating");
  const [viewMode, setViewMode] = useState("grid");

  const { data, isLoading, error } = useQuery(
    ["vendors", { searchTerm, cuisineFilter, regionFilter, sortBy }],
    () =>
      vendorsAPI.getVendors({
        cuisine: cuisineFilter,
        region: regionFilter,
        sortBy,
        sortOrder: "DESC",
      }),
    {
      keepPreviousData: true,
    }
  );

  const vendors = data?.data?.data?.vendors || [];

  // Debug logging removed - vendors currently don't have profile images uploaded

  const cuisineTypes = [
    "Guyanese",
    "Indian",
    "Chinese",
    "Caribbean",
    "Creole",
    "International",
    "Mixed",
  ];

  const regions = [
    "Georgetown",
    "East Coast",
    "West Coast",
    "Berbice",
    "Essequibo",
  ];

  if (isLoading) {
    return (
      <VendorsContainer>
        <VendorsContent>
          <PageHeader>
            <h1>Our Vendors</h1>
            <p>Discover authentic Guyanese food vendors in your area</p>
          </PageHeader>
          <LoadingState>
            <div className="loading-spinner" />
            Loading vendors...
          </LoadingState>
        </VendorsContent>
      </VendorsContainer>
    );
  }

  if (error) {
    return (
      <VendorsContainer>
        <VendorsContent>
          <PageHeader>
            <h1>Our Vendors</h1>
            <p>Discover authentic Guyanese food vendors in your area</p>
          </PageHeader>
          <ErrorState>
            <h3>Something went wrong</h3>
            <p>Unable to load vendors. Please try again later.</p>
          </ErrorState>
        </VendorsContent>
      </VendorsContainer>
    );
  }

  return (
    <VendorsContainer>
      <VendorsContent>
        <PageHeader>
          <h1>Our Vendors</h1>
          <p>Discover authentic Guyanese food vendors in your area</p>
        </PageHeader>

        <SearchAndFilters>
          <SearchBar>
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search vendors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchBar>

          <FiltersRow>
            <FilterGroup>
              <label>Cuisine Type</label>
              <select
                value={cuisineFilter}
                onChange={(e) => setCuisineFilter(e.target.value)}
              >
                <option value="">All Cuisines</option>
                {cuisineTypes.map((cuisine) => (
                  <option key={cuisine} value={cuisine}>
                    {cuisine}
                  </option>
                ))}
              </select>
            </FilterGroup>

            <FilterGroup>
              <label>Region</label>
              <select
                value={regionFilter}
                onChange={(e) => setRegionFilter(e.target.value)}
              >
                <option value="">All Regions</option>
                {regions.map((region) => (
                  <option key={region} value={region}>
                    {region}
                  </option>
                ))}
              </select>
            </FilterGroup>

            <FilterGroup>
              <label>Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="rating">Rating</option>
                <option value="total_reviews">Most Reviews</option>
                <option value="created_at">Newest</option>
                <option value="business_name">Name</option>
              </select>
            </FilterGroup>

            <ViewToggle>
              <button
                className={viewMode === "grid" ? "active" : ""}
                onClick={() => setViewMode("grid")}
                aria-label="Grid view"
              >
                <FiGrid />
              </button>
              <button
                className={viewMode === "list" ? "active" : ""}
                onClick={() => setViewMode("list")}
                aria-label="List view"
              >
                <FiList />
              </button>
            </ViewToggle>
          </FiltersRow>
        </SearchAndFilters>

        {vendors.length === 0 ? (
          <EmptyState>
            <h3>No vendors found</h3>
            <p>Try adjusting your search criteria or check back later.</p>
          </EmptyState>
        ) : (
          <VendorsGrid className={viewMode === "list" ? "list-view" : ""}>
            {vendors.map((vendor, index) => (
              <VendorCard
                key={vendor.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <VendorImage>
                  {vendor.profile_image_url ? (
                    <img
                      src={vendor.profile_image_url}
                      alt={vendor.businessName || vendor.business_name}
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.nextSibling.style.display = "flex";
                      }}
                    />
                  ) : null}
                  <div
                    className="placeholder"
                    style={{
                      display: vendor.profile_image_url ? "none" : "flex",
                    }}
                  >
                    <span>🍽️</span>
                    <span>
                      {(vendor.businessName || vendor.business_name).charAt(0)}
                    </span>
                  </div>
                </VendorImage>
                <VendorInfo>
                  <h3>{vendor.businessName || vendor.business_name}</h3>
                  <div className="cuisine">
                    {vendor.cuisineType || vendor.cuisine_type}
                  </div>

                  <div className="location">
                    <FiMapPin />
                    {vendor.businessAddress || vendor.business_address}
                  </div>

                  {vendor.description && (
                    <div className="description">{vendor.description}</div>
                  )}

                  <ViewButton to={`/vendors/${vendor.id}`}>
                    View Menu & Plans
                  </ViewButton>
                </VendorInfo>
              </VendorCard>
            ))}
          </VendorsGrid>
        )}
      </VendorsContent>
    </VendorsContainer>
  );
};

export default Vendors;
